import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box, Grid, useMediaQuery, useTheme } from "@mui/material";
import {
  authSelector,
  authStatusSelector,
  qrcVerifiedSelector,
} from "../../services/redux/slices/authSlice";
import Sidebar from "../../pages/common-features/Sidebar";
import { HeaderWelcome } from "../../pages/Header";
import HorizontalLinearStepper from "../../pages/common-features/HorizontalLinearStepper";
import {
  setActiveHorizontalIndex,
  clearPartnerRouteParams,
  setIsBypassLogin,
  setPartnerRouteParams,
  setDisplayHorizontalSteps,
  setLeadSequenceId,
  setIsPreLogin,
  setIsEligibilityCheckSubmitted,
  setTrackingId,
  setCollateralId,
  setreferenceOneId,
  setreferenceTwoId,
  setActiveVerticalIndex,
  updateFormData,
  setLastVerifiedPhoneNumber,
  setLastVerifiedEmail,
  setIsMobileVerified,
  setIsEmailVerified,
  setFetchUserApplicationData,
  setApplicationId,
  setApplicantId,
  setAssignedSMIdValue,
  setApplicationSource,
  setCheckIsLeadApiInvoked,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import {
  fetchUserApplications,
  fetchUserApplicationsFormData,
} from "../../services/api/service/userApplicationService";
import { getCombinedApplicationSteps } from "../ui/StepperDyanmicForm/helper";
import { ROUTES } from "../../routes/routerConfig";
import { RootState } from "../../services/redux/store";
import RightLayoutOutlet from "../common/RightLayoutOutlet";
import DialogStatusPopup from "../common/DialogStatusPopup";
import LoanDocumentsIcon from "../../assets/svgTabIcons/LoanDocumentsIcon";
import ProcessingFeeIcon from "../../assets/svgTabIcons/ProcessingFeeIcon";
import preLoginTopBanner from "../../assets/svgIcons/pre-login-banner-mobile.svg";
import Header from "../../pages/Header";
import { useTriggerData } from "../../hooks/useTriggerData";
import {
  setTriggerData,
  setUserAuth,
  setRequestSignature,
} from "../../services/redux/slices/authSlice";
import { decrypt } from "../../utils/crypto";
import ContactUsIcon from "../../assets/svgTabIcons/ContactUsIcon";

// Responsive sidebar width based on screen size
const SIDEBAR_WIDTH = {
  xs: 0,
  sm: 80,
};

const DashboardLayoutJourny: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const isAuthenticated = useSelector(authStatusSelector);
  const isQrcVerified = useSelector(qrcVerifiedSelector);
  const isBypassLogin = useSelector(
    (state: RootState) => state?.stepper?.isBypassLogin
  );
  const activeHorizontalIndex = useSelector(
    (state: RootState) => state?.stepper?.activeHorizontalIndex
  );
  const isPreLogin = useSelector(
    (state: RootState) => state?.stepper?.isPreLogin
  );
  const trackingId = useSelector(
    (state: RootState) => state?.stepper?.trackingId
  );
  const { isTriggerDataEmpty, triggerData }: any = useTriggerData();
  const isApplicant = isTriggerDataEmpty ? true : triggerData?.toUser;
  const { userProfile, token } = useSelector(authSelector);
  const isPublicApplyNowRoute = location.pathname === ROUTES.ApplyNow.locationPath;
  // A pre-login visitor verified via the Help Center OTP flow (see QrcRoutes) should see
  // the actual Contact Us page content, not the loan-application eligibility wizard below.
  const isQrcVerifiedContactUsRoute =
    isQrcVerified && location.pathname.startsWith(ROUTES.ContactUs.index);
  const isContactUsRoute = location.pathname.startsWith(ROUTES.ContactUs.index);

  let sidebarNavItems = [
    {
      id: "dashboard",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <LoanDocumentsIcon color="#191515ff" />
        </svg>
      ),
      path: "/application/existing-application",
      label: "Applications",
    },
    {
      id: "applications",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 20 20"
          fill="none"
          color="#191515ff"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ProcessingFeeIcon color="#191515ff" />
        </svg>
      ),
      path: "/dashboard",
      label: "Loans",
    },
    {
      id: "contact-us",
      icon: (
        <svg
          width="28"
          height="28"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ContactUsIcon color="#191515ff" />
        </svg>
      ),
      path: "/",
      label: "Contact Us",
      children: [
        { id: "raise-request", path: "/contact-us/raise-request", label: "Raise a Request" },
        { id: "view-tickets", path: "/contact-us/view-tickets", label: "View Tickets" },
      ],
    },
  ];

  const mountedRef = useRef(false);
  const prevSearchRef = useRef<string>(location.search);

  // Mobile app deep link: _wsAuth = "token&?&xRequestSignature=..&preLogin=..&appType=..".
  const handleWsAuth = async (wsAuth: string) => {
    try {
      const decryptedWsAuth = decrypt(wsAuth);
      if (!decryptedWsAuth) return;

      const parts = decryptedWsAuth.split("&");
      const getPartParam = (index: number, key: string) =>
        decodeURIComponent(
          new URLSearchParams(
            (parts?.[index] || "").trim().replace(/\+/g, "%2B")
          ).get(key) || ""
        );
      const tokenFromWsAuth = (parts?.[0] || "").trim();
      const requestSignature = getPartParam(2, "xRequestSignature");
      const preLogin = getPartParam(3, "preLogin");
      const appType = getPartParam(4, "appType");
      sessionStorage.setItem("deviceType", "mobile");

      // UnAuthRoutes skips its authenticated redirect while _wsAuth is in the
      // URL, so the navigate below decides where the user lands.
      const applyAuth = () => {
        if (tokenFromWsAuth) {
          dispatch(setUserAuth(`Bearer ${tokenFromWsAuth}`));
          dispatch(setRequestSignature(requestSignature));
        }
      };
      // Same session setup as EligibilityConfirmation's guest/bypass login.
      // No explicit apply-now navigate: like that flow, the user goes to
      // existing-application (where UnAuthRoutes sends an authenticated user)
      // and this layout's isBypassLogin/isPreLogin redirect takes it from there.
      if (preLogin == "true") {
        // Auth first (as EligibilityConfirmation does) so the fetches below
        // run as this user; UnAuthRoutes holds its redirect while _wsAuth is
        // in the URL.
        applyAuth();
        const fetchToken: any = process.env.REACT_APP_FETCH_TOKEN;

        // A fresh webview has no persisted trackingId, and the stepper only
        // prefills form data when one is set — so resolve it from the user's
        // latest application, same as ExistingApplication does.
        let applicationId = trackingId;
        if (!applicationId) {
          const appsRes: any = await fetchUserApplications(fetchToken);
          const latestApp = appsRes?.data?.applications?.[0];
          applicationId = latestApp?.submissionId || "";
          dispatch(setTrackingId(applicationId));
          dispatch(setCollateralId(latestApp?.collateralId || null));
          dispatch(setreferenceOneId(latestApp?.referenceOneId || null));
          dispatch(setreferenceTwoId(latestApp?.referenceTwoId || null));
        }

        const res: any = await fetchUserApplicationsFormData(
          fetchToken,
          applicationId
        );
        const apps = res?.data?.applications || [];
        const app = apps[0];

        // In EligibilityConfirmation the form data is already in redux (the
        // user just typed it). Here nothing is, and DynamicForm's Formik only
        // reinitializes on a few steps — so seed formData (and the lead /
        // verification state ExistingApplication's resume sets) before the
        // apply-now form mounts.
        let apiFormData: any = {};
        app?.horizontalSteps?.forEach((hStep: any) =>
          hStep?.verticalSteps?.forEach((vStep: any) =>
            vStep?.fields?.forEach((field: any) => {
              apiFormData = { ...apiFormData, ...field };
            })
          )
        );
        dispatch(updateFormData(apiFormData));
        dispatch(setLastVerifiedPhoneNumber(apiFormData?.phone_number));
        dispatch(setLastVerifiedEmail(apiFormData?.email));
        dispatch(setIsMobileVerified(!!apiFormData?.phone_number));
        dispatch(setIsEmailVerified(!!apiFormData?.email));

        if (app) dispatch(setFetchUserApplicationData(app));
        dispatch(setApplicationId(app?.applicationId));
        dispatch(setApplicantId(app?.applicantId));
        dispatch(setAssignedSMIdValue(app?.assignedSMId));
        dispatch(setApplicationSource(app?.source || ""));
        dispatch(
          setCheckIsLeadApiInvoked({
            key: "ASSIGN_SM",
            value: !!app?.assignedSMId,
          })
        );
        dispatch(
          setCheckIsLeadApiInvoked({
            key: "CREATE_LEAD",
            value: !!(app?.assignedSMId && app?.leadId),
          })
        );
        dispatch(
          setCheckIsLeadApiInvoked({
            key: "LEAD_TO_APPLICATION",
            value: !!(
              app?.applicantId &&
              app?.applicationId &&
              app?.applicationDisplayCode
            ),
          })
        );

        dispatch(setIsBypassLogin(true));
        dispatch(setIsPreLogin(true));
        dispatch(setIsEligibilityCheckSubmitted(true));
        dispatch(setDisplayHorizontalSteps(getCombinedApplicationSteps(app)));
        dispatch(setLeadSequenceId(app?.leadId));
        dispatch(setActiveHorizontalIndex(1));
        dispatch(setActiveVerticalIndex(0));
        navigate("/application/existing-application", { replace: true });
        return;
      }

      dispatch(setIsBypassLogin(false));
      applyAuth();
      if (appType === "new_application") {
        navigate("/application", { replace: true });
      } else {
        navigate("/application/existing-application", { replace: true });
      }
    } catch (error) {
      console.error("Failed to decrypt _wsAuth token", error);
    }
  };

  useEffect(() => {
    const currentSearch = location.search || "";
    const shouldRun = !mountedRef.current || prevSearchRef.current !== currentSearch;
    if (!shouldRun) return;
    prevSearchRef.current = currentSearch;

    const urlParams = new URLSearchParams(currentSearch);
    const partnerCode = urlParams.get("code") || "";
    const partnerName = urlParams.get("name") || "";

    if (isPublicApplyNowRoute) {
      if (partnerCode || partnerName) {
        dispatch(setPartnerRouteParams({ partnerCode, partnerName }));
      } else {
        dispatch(clearPartnerRouteParams());
      }

      const wsAuth = urlParams.get("_wsAuth");
      if (wsAuth) {
        // The mobile app link handles its own navigation, so skip the
        // apply-now query cleanup below (it would override it).
        handleWsAuth(wsAuth);
        mountedRef.current = true;
        return;
      }

      // Preserve only specific query parameters
      const preservedParams = new URLSearchParams();
      const paramsToPreserve = [
        "productName",
        "leadoriginsource",
        "source",
        "subsubsource",
        "utm_source",
        "utm_campaign",
        "utm_partner_lead_id",
        "name",
        "code",
        "eligilibity_campaign",
        "provisional_campaign",
        "consent_campaign",
        "adgroup",
        "keywords",
        "gclid"
      ];

      paramsToPreserve.forEach((param) => {
        const value = urlParams.get(param);
        if (value) preservedParams.set(param, value);
      });

      const query = preservedParams.toString() ? `?${preservedParams.toString()}` : "";
      navigate(`${ROUTES.ApplyNow.locationPath}${query}`, { replace: true });
      dispatch(setIsBypassLogin(false));
    } else {
      if (urlParams.get("document_verified") == "true") {
        if (isBypassLogin) {
          if (isTriggerDataEmpty) {
            if (window.location.pathname === "/application/summary") {
                navigate("/application/summary?document_verified=true", { replace: true });
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else if (window.location.pathname === "/application/loan-summary") {
              navigate("/application/loan-summary?document_verified=true", { replace: true });
              //dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else if (window.location.pathname === "/application/upload-document") {
              navigate("/application/upload-document?document_verified=true", { replace: true });
            }
            else{
              navigate("/application/apply-now?document_verified=true", { replace: true });
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            }
          } else {
             if (window.location.pathname === "/application/summary") {
              navigate("/application/summary?document_verified=true", { replace: true });
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            }else if (window.location.pathname === "/application/loan-summary") {
              navigate("/application/loan-summary?document_verified=true", { replace: true });
              //dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else if (window.location.pathname === "/application/upload-document") {
              navigate("/application/upload-document?document_verified=true", { replace: true });
            }
            else{
              navigate("/application/apply-now?document_verified=true", { replace: true });
              dispatch(setActiveHorizontalIndex(3));
            }
          }
        }
      } else if (urlParams.get("consent") == "true") {
        if (window.location.pathname === "/application/apply-now") {
          navigate("/application/apply-now?consent=true", { replace: true });
          dispatch(setActiveHorizontalIndex(3));
        } else if (window.location.pathname === "/application/upload-document") {
          navigate("/application/upload-document?consent=true", { replace: true });
        }
        else if (window.location.pathname === "/application/summary") {
          navigate("/application/summary?consent=true", { replace: true });
        }else if (window.location.pathname === "/application/loan-summary") {
          navigate("/application/loan-summary?consent=true", { replace: true });
        }
      } else {
        if (isBypassLogin) {
          if (isTriggerDataEmpty) {
          //navigate("/application/summary");
            // if(isPreLogin) {
            //   navigate("/application/apply-now",{ replace: true });
            // } else {
            //   navigate("/application/existing-application",{ replace: true });
            // }
           
            if (window.location.pathname === "/application/summary") {
              navigate("/application/summary", { replace: true });
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else if (window.location.pathname === "/application/loan-summary") {
              navigate("/application/loan-summary", { replace: true });
              //dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else if(window.location.pathname === "/application/apply-now") {
              navigate("/application/apply-now", { replace: true });
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else if(isPreLogin) {
              navigate("/application/apply-now",{ replace: true });
            } else {
              navigate("/application/existing-application",{ replace: true });
            }

            if (activeHorizontalIndex == 0) {
              dispatch(setActiveHorizontalIndex(1));
            } else {
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            }
          }else if (window.location.pathname === "/application/loan-summary") {
              navigate("/application/loan-summary", { replace: true });
              //dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            } else {
            navigate("/application/summary", { replace: true });
            dispatch(setActiveHorizontalIndex(3));
          }
        } else {
          if (location.pathname == "/application/apply-now") {
            if (activeHorizontalIndex >= 0) {
              navigate(location.pathname, { replace: true });
              dispatch(setActiveHorizontalIndex(activeHorizontalIndex));
            }
          } else if (location.pathname == "/application") {
            navigate(location.pathname, { replace: true });
            dispatch(setIsBypassLogin(false));
          } else if (location.pathname == "/application/view-all-applications") {
            navigate(location.pathname, { replace: true });
            dispatch(setIsBypassLogin(false));
          }

          if (triggerData?.toUser) {
            if (triggerData?.toUser === false) {
              navigate("/application/summary", { replace: true });
              dispatch(setActiveHorizontalIndex(3));
            } else {
              // dispatch(setTriggerData({}));
              // navigate("/application/existing-application");
            }
          }
        }
      }
    }

    mountedRef.current = true;
  }, [dispatch, isPublicApplyNowRoute, isBypassLogin, isTriggerDataEmpty, triggerData, activeHorizontalIndex, token, location.search]);

  const showSidebar = React.useMemo(() => {
    if (isAuthenticated) {
      return (
        <>
          {isApplicant && (
            <Box
              sx={{
                display: {
                  xs: "none",
                  sm: "block",
                },
              }}
            >
              <Sidebar navItems={sidebarNavItems} />
            </Box>
          )}
        </>
      );
    }
  }, [isAuthenticated, isTriggerDataEmpty, triggerData, isApplicant]);
  const deviceType = sessionStorage.getItem("deviceType");
  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        background: "#f5f6fa",
        // "clip" on Contact Us so the sticky "Before you raise a request" rail works: "hidden"
        // makes this a scroll container (overflow-y computes to auto), which pins sticky
        // descendants to a box that never scrolls — measured, the rail scrolled away instead of
        // sticking. "clip" suppresses horizontal scroll the same way without that side effect.
        // Everywhere else keeps "hidden", so no other page's overflow behaviour changes.
        overflowX: isContactUsRoute ? "clip" : "hidden",
      }}
    >
      {/* Sidebar - Hidden on mobile */}
      {deviceType !== "mobile" && showSidebar}
        <Box sx={{ width: "100%", flex: 1, display: "flex", flexDirection: "column" }}>
          {/* mobile only pre login banner image */}
          {isXsOrSm && !isAuthenticated && location.pathname === "/apply-now" ? (
            <img
              src={preLoginTopBanner}
              alt="Company Logo"
              style={{ 
                width: "100%", 
              }}
            />
          ) : (
            <></>
          )}

          {/* Main Content */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              marginLeft: {
                xs: 0,
                sm: `${
                  isAuthenticated ? (isApplicant ? SIDEBAR_WIDTH.sm : 0) : 0
                }px`,
              },
              padding:
                isXsOrSm && location.pathname === "/application/apply-now" &&  deviceType !== "mobile"
                  ? "16px 8px"
                  :isXsOrSm && location.pathname === "/application/apply-now" &&  deviceType === "mobile"
                  ? "0px 8px"
                  : isXsOrSm && deviceType !== "mobile"
                  ? "16px 16px"
                  : isXsOrSm && deviceType === "mobile"
                  ? "0px 8px"
                  : "8px 16px",
              maxWidth: "100%",
              marginTop:deviceType === "mobile" && isXsOrSm?"-60px":"0",
            }}
          >
            {isApplicant && deviceType !== "mobile" && (
              <>
                {!isAuthenticated && location.pathname === "/apply-now" ? (
                  <Header />
                ) : (
                  <HeaderWelcome
                    isAuthenticated={isAuthenticated}
                    userName={userProfile?.name}
                    notificationCount={2}
                  />
                )}
              </>
            )}

            <Box sx={{ flex: 1 }}>
              {!isAuthenticated && !isQrcVerifiedContactUsRoute ? (
                <>
                  <HorizontalLinearStepper status={true} />
                </>
              ) : (
                <Grid
                  container
                  spacing={2}
                  sx={{
                    // Clears HeaderWelcome, which is position:absolute with an 80px bar on small
                    // screens. Two cases reserve that space for a header that isn't there:
                    // deviceType === "mobile" (the header's own render condition excludes it), and
                    // the QRC Contact Us route, which brings its own hero + Logout pill instead.
                    pt:
                      isXsOrSm &&
                      isApplicant &&
                      deviceType !== "mobile" &&
                      !isQrcVerifiedContactUsRoute
                        ? "80px"
                        : 0,
                  }}
                >
                  <Grid
                    item
                    sm={location.pathname === "/dashboard" ? 9 : 12}
                    sx={{ width: "100%", maxWidth: { xs: "100vw", md: "auto" } }}
                  >
                    <Outlet />
                  </Grid>

                  {location.pathname === "/dashboard" && (
                    <Grid item sm={3}>
                      {!isXsOrSm && <RightLayoutOutlet />}
                    </Grid>
                  )}
                </Grid>
              )}
            </Box>
          </Box>
        </Box>

      <DialogStatusPopup />
    </Box>
  );
};

export default DashboardLayoutJourny;